/**
 * Browser telemetry (T07 §9.4, CR-13). Import this file FIRST in main.tsx:  import "./telemetry";
 * Everything is posted to the same origin (/api/telemetry/v1/traces and /api/telemetry/v1/logs), so the
 * browser never talks to a telemetry vendor and the backend stays the only deployed process (T6).
 */
import { context, trace, SpanStatusCode, type Span } from "@opentelemetry/api";
import { logs, SeverityNumber, type Logger } from "@opentelemetry/api-logs";
import { WebTracerProvider, BatchSpanProcessor } from "@opentelemetry/sdk-trace-web";
import { LoggerProvider, BatchLogRecordProcessor } from "@opentelemetry/sdk-logs";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { AWSXRayIdGenerator } from "@opentelemetry/id-generator-aws-xray";
import { TELEMETRY_PATHS } from "@chartvn/schema";

const SERVICE = "chartvn-web";
const VERSION = import.meta.env.VITE_APP_VERSION ?? "dev";
const ENV = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE;
const ENABLED = import.meta.env.VITE_TELEMETRY_ENABLED !== "false";

/** Per-tab session id: random, opaque, lives only in sessionStorage. Never a name or email. */
const sessionId: string = (() => {
  try {
    const k = "chartvn.session_id";
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(k, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
})();

let userId: string | undefined; // opaque server user id, set after the session loads
export function setTelemetryUser(id: string | undefined): void {
  userId = id;
}

const resource = resourceFromAttributes({
  "service.name": SERVICE,
  "service.version": VERSION,
  "deployment.environment.name": ENV,
  "session.id": sessionId,
});

const tracerProvider = new WebTracerProvider({
  resource,
  idGenerator: new AWSXRayIdGenerator(),
  spanProcessors: ENABLED
    ? [
        new BatchSpanProcessor(new OTLPTraceExporter({ url: TELEMETRY_PATHS.traces }), {
          scheduledDelayMillis: 2000,
          maxExportBatchSize: 64,
        }),
      ]
    : [],
});
tracerProvider.register({ contextManager: new ZoneContextManager() });

const loggerProvider = new LoggerProvider({
  resource,
  processors: ENABLED
    ? // sdk-logs 0.222.0 takes a single options object; the (exporter, config) form was removed.
      [
        new BatchLogRecordProcessor({
          exporter: new OTLPLogExporter({ url: TELEMETRY_PATHS.logs }),
          scheduledDelayMillis: 2000,
        }),
      ]
    : [],
});
logs.setGlobalLoggerProvider(loggerProvider);

registerInstrumentations({
  instrumentations: [
    getWebAutoInstrumentations({
      "@opentelemetry/instrumentation-document-load": {},
      "@opentelemetry/instrumentation-user-interaction": {
        eventNames: ["click", "submit", "change"],
        shouldPreventSpanCreation: (_event, element, span) => {
          // Interaction telemetry records WHAT was clicked, never what was typed.
          const label =
            element.getAttribute("data-telemetry") ??
            element.getAttribute("aria-label") ??
            element.getAttribute("data-testid") ??
            element.id ??
            element.tagName.toLowerCase();
          span.setAttribute("ui.target", label.slice(0, 80));
          span.setAttribute("ui.route", location.pathname);
          return false;
        },
      },
      "@opentelemetry/instrumentation-fetch": {
        // Same-origin requests get traceparent automatically; nothing cross-origin is allowed.
        propagateTraceHeaderCorsUrls: [],
        ignoreUrls: [/\/api\/telemetry\//],
        clearTimingResources: true,
        applyCustomAttributesOnSpan: (span) => {
          if (userId) span.setAttribute("user.id", userId);
        },
      },
      "@opentelemetry/instrumentation-xml-http-request": {
        ignoreUrls: [/\/api\/telemetry\//],
      },
    }),
  ],
});

const tracer = trace.getTracer(SERVICE, VERSION);
const logger: Logger = logs.getLogger(SERVICE, VERSION);

function commonAttrs(extra: Record<string, string | number | boolean | undefined> = {}) {
  const sc = trace.getSpan(context.active())?.spanContext();
  return {
    "session.id": sessionId,
    ...(userId ? { "user.id": userId } : {}),
    "url.path": location.pathname, // path only; query strings may carry identifiers
    ...(sc ? { trace_id: sc.traceId, span_id: sc.spanId } : {}),
    ...extra,
  };
}

/** Structured error report used by window handlers and the React ErrorBoundary. */
export function reportError(err: unknown, source: string, componentStack?: string): void {
  const e = err instanceof Error ? err : new Error(String(err));
  logger.emit({
    severityNumber: SeverityNumber.ERROR,
    severityText: "ERROR",
    body: e.message.slice(0, 500),
    attributes: commonAttrs({
      "error.type": e.name,
      "error.message": e.message.slice(0, 500),
      "error.stack": (e.stack ?? "").slice(0, 4000),
      "error.source": source,
      ...(componentStack ? { "react.component_stack": componentStack.slice(0, 2000) } : {}),
    }),
  });
  const span = trace.getSpan(context.active());
  span?.recordException(e);
  span?.setStatus({ code: SpanStatusCode.ERROR, message: e.name });
}

window.addEventListener("error", (ev) => reportError(ev.error ?? ev.message, "window.onerror"));
window.addEventListener("unhandledrejection", (ev) => reportError(ev.reason, "unhandledrejection"));

/** Called by the router on every navigation (app/router.tsx). */
export function reportNavigation(pathname: string): void {
  const span: Span = tracer.startSpan("navigation", {
    attributes: commonAttrs({ "url.path": pathname }),
  });
  span.end();
  logger.emit({
    severityNumber: SeverityNumber.INFO,
    severityText: "INFO",
    body: "route change",
    attributes: commonAttrs({ "url.path": pathname }),
  });
}

/* eslint-disable no-console -- the one sanctioned console sink (C07): silent in production except errors */
/** Dev console policy: everything goes through here; production keeps only errors. */
export const log = {
  debug: (...a: unknown[]) => {
    if (import.meta.env.DEV) console.debug(...a);
  },
  info: (...a: unknown[]) => {
    if (import.meta.env.DEV) console.info(...a);
  },
  warn: (...a: unknown[]) => {
    if (import.meta.env.DEV) console.warn(...a);
  },
  error: (...a: unknown[]) => {
    console.error(...a);
  },
};
/* eslint-enable no-console */

// Flush on tab close so the last spans/errors are not lost.
window.addEventListener("pagehide", () => {
  void tracerProvider.forceFlush();
  void loggerProvider.forceFlush();
});
